import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getDetailExcoPusat } from "../../../config/Redux/ExcoPusat/actions";
import { Link } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExcoSkeletonLoading from "./ExcoSkeletonCard";
import "./ExcoCardView.css";

/**
 * Reusable Card View Component for EXCO pages (Provinsi, KabKot, etc.)
 * @param {Object} props
 * @param {boolean} props.loading - Loading state
 * @param {Array} props.data - Array of data items to display
 * @param {number} props.currentPage - Current page number for pagination
 * @param {string} props.type - Type of EXCO: "provinsi" | "kabkot" | "kecamatan"
 * @param {string} props.nameExco - Name for detail navigation
 * @param {number} props.skeletonCount - Number of skeleton cards to show when loading (default: 3)
 */
const ExcoCardView = ({
  loading,
  data,
  currentPage = 1,
  type = "provinsi",
  nameExco = "EXCO",
  skeletonCount = 3,
}) => {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const handleClickDetail = async (tingkat, id) => {
    const response = await dispatch(getDetailExcoPusat(tingkat, id));
    const idKantorExco = response.payload?.id_kantor_exco || response.payload?.data?.id_kantor_exco;
    if (idKantorExco) {
      navigate(
        `/detail-exco-pusat/${idKantorExco}/${tingkat}/${id}`,
        {
          state: {
            nameExco: nameExco,
          },
        },
      );
    }
  };

  const persentase = (penyebut, pembagi) => {
    let persen = 0;
    if (parseInt(pembagi) !== 0) {
      persen = ((parseInt(penyebut) / parseInt(pembagi)) * 100).toFixed(0);
    }
    return persen;
  };

  const formatNumber = (num) => {
    if (typeof num === "string" && isNaN(parseInt(num))) return num;
    return parseInt(num)?.toLocaleString().replace(/,/g, ".") || "0";
  };

  const getProgressClass = (percent) => {
    if (percent < 50) return "red";
    if (percent < 100) return "yellow";
    return "green";
  };

  // Get location name based on type
  const getLocationName = (item) => {
    if (type === "provinsi") {
      return item?.alamat?.nama_provinsi;
    } else if (type === "kabkot") {
      return item?.alamat?.nama_kab_kot || item?.nama_kantor;
    } else if (type === "kecamatan") {
      return item?.alamat?.nama_kecamatan || item?.nama_kantor;
    } else if (type === "kelurahan") {
      return item?.alamat?.nama_kelurahan || item?.nama_kantor;
    } else if (type === "puk") {
      return (
        item?.alamat?.nama_rtrw ||
        item?.tingkat_puk_industri ||
        item?.alamat?.nama_kantor
      );
    } else if (type === "rtrw") {
      return item?.alamat?.tingkat_rt_rw || "";
    }
    return item?.nama_kantor;
  };

  // Get location breadcrumb/subtitle
  const getLocationSubtitle = (item) => {
    if (type === "kabkot") {
      return `Provinsi: ${item?.alamat?.nama_provinsi || ""}`;
    } else if (type === "kecamatan") {
      return `Provinsi: ${item?.alamat?.nama_provinsi || ""} | Kab/Kota: ${item?.alamat?.nama_kab_kot || ""}`;
    } else if (type === "kelurahan") {
      return `Provinsi: ${item?.alamat?.nama_provinsi || ""} | Kab/Kota: ${item?.alamat?.nama_kab_kot || ""} | Kecamatan: ${item?.alamat?.nama_kecamatan || ""}`;
    } else if (type === "rtrw") {
      return `Provinsi: ${item?.alamat?.nama_provinsi || ""} | Kab/Kota: ${item?.alamat?.nama_kab_kot || ""} | Kecamatan: ${item?.alamat?.nama_kecamatan || ""} | Kel/Desa: ${item?.alamat?.nama_kel_desa || item?.alamat?.nama_kelurahan || ""}`;
    } else if (type === "puk") {
      return `Provinsi: ${item?.alamat?.nama_provinsi || ""} | Kab/Kota: ${item?.alamat?.nama_kab_kot || ""}`;
    }
    return null;
  };

  // Get link for member list
  const getMemberListLink = (item) => {
    if (type === "provinsi") {
      return `/daftar-anggota/2/${item?.alamat?.id_provinsi}/${item?.alamat?.nama_provinsi}`;
    } else if (type === "kabkot") {
      return `/daftar-anggota/3/${item?.alamat?.id_kabkot || item?.id_kabkot}/${item?.alamat?.nama_kab_kot || item?.nama_kantor}`;
    } else if (type === "kecamatan") {
      return `/daftar-anggota/4/${item?.alamat?.id_kecamatan || item?.id_kecamatan}/${item?.alamat?.nama_kecamatan || item?.nama_kantor}`;
    } else if (type === "kelurahan") {
      return `/daftar-anggota/5/${item?.alamat?.id_kelurahan || item?.id_kelurahan}/${item?.alamat?.nama_kelurahan || item?.nama_kantor}`;
    }
    return "#";
  };

  // Get detail params based on type
  const getDetailParams = (item) => {
    // console.log({ item });
    if (type === "provinsi") {
      return {
        tingkat: item.tingkat_exco?.id || 2,
        id: item.alamat?.id_provinsi,
      };
    } else if (type === "kabkot") {
      return {
        tingkat: item.tingkat_exco?.id || 3,
        id: item.alamat?.id_kabkot || item?.id_kabkot,
      };
    } else if (type === "kecamatan") {
      return {
        tingkat: item.tingkat_exco?.id || 4,
        id: item.alamat?.id_kecamatan || item?.id_kecamatan,
      };
    } else if (type === "kelurahan") {
      return {
        tingkat: item.tingkat_exco?.id || 5,
        id: item.alamat?.id_kelurahan || item?.id_kelurahan,
      };
    } else if (type === "rtrw") {
      return {
        tingkat: item.tingkat_exco?.id || 6, // RT/RW or PUK level
        id: item.id_kantor_exco,
      };
    } else if (type === "puk") {
      return {
        tingkat: item.tingkat_exco?.id || 7, // RT/RW or PUK level
        id: item.id_kantor_exco,
      };
    }
    return { tingkat: 1, id: 0 };
  };

  // Get telusuri (explore) link based on type
  const getTelusuriLink = (item) => {
    const idProvinsi = item?.alamat?.id_provinsi;
    const idKabkot = item?.alamat?.id_kabkot || item?.id_kabkot;
    const idKecamatan = item?.alamat?.id_kecamatan || item?.id_kecamatan;

    if (type === "provinsi") {
      return `/kab-kot?province=${idProvinsi}&page=1`;
    } else if (type === "kabkot") {
      return `/kecamatan?province=${idProvinsi}&page=1&city=${idKabkot}`;
    } else if (type === "kecamatan") {
      return `/kelurahan?province=${idProvinsi}&page=1&city=${idKabkot}&kecamatan=${idKecamatan}`;
    }
    return "#";
  };

  // Get telusuri button label based on type
  const getTelusuriLabel = () => {
    if (type === "provinsi") {
      return "Telusuri Kab/Kota";
    } else if (type === "kabkot") {
      return "Telusuri Kecamatan";
    } else if (type === "kecamatan") {
      return "Telusuri Kelurahan/Desa";
    }
    return "Telusuri";
  };

  // Show skeleton loading when loading
  if (loading) {
    return <ExcoSkeletonLoading count={skeletonCount} />;
  }

  if (!data?.length) {
    return (
      <div className="exco-card-view-empty">
        <p>Data tidak ditemukan</p>
      </div>
    );
  }

  return (
    <div className="exco-card-view-container">
      {type !== "rtrw" && type !== "puk" && (
        <div className="exco-card-view-legend">
          <CheckCircleIcon
            style={{ color: "rgb(240, 101, 36)", fontSize: "16px" }}
          />
          <span>Daerah memiliki EXCO terverifikasi Partai Buruh</span>
        </div>
      )}

      {data?.map((item, index) => {
        const number = index + 1 + 10 * (currentPage - 1);
        const progress = persentase(item?.total_anggota_2029, item?.target_pb);
        const selisih = parseInt(item?.total_anggota_2029 - item?.target_pb);
        const detailParams = getDetailParams(item);

        // Calculate Kepengurusan filled count (4 items)
        const kepengurusanCount = [
          item?.nomer_sk_pengurus && item?.nomer_sk_pengurus !== "Belum Terisi", // 1. Nomor SK Pengurus Harian
          item?.tanggal_sk && item?.tanggal_sk !== "Belum Terisi", // 2. Tanggal SK Pengurus Harian
          item?.total_pengurus && parseInt(item?.total_pengurus) > 0, // 3. Jumlah Pengurus
          item?.status_dokumen?.sk_pengurus === 1, // 4. Dokumen SK Pengurus Harian
        ].filter(Boolean).length;

        // Calculate Kantor filled count
        // For Kelurahan: only count Alamat (1/1)
        // For others: count Alamat, Sewa Kantor, Domisili (X/3)
        const kantorCount =
          type === "kelurahan" || type === "puk" || type === "rtrw"
            ? item?.status_alamat === 1
              ? 1
              : 0
            : [
                item?.status_alamat === 1, // 1. Alamat
                item?.status_dokumen?.sewa_kantor === 1, // 2. Dokumen Perjanjian Sewa Kantor
                item?.status_dokumen?.domisili === 1, // 3. Dokumen Suket Domisili Kantor
              ].filter(Boolean).length;
        const kantorTotal =
          type === "kelurahan" || type === "puk" || type === "rtrw" ? 1 : 3;

        // Calculate Rekening Bank filled count (3 items)
        // Note: All 3 items use the same rekening field in the current API
        const rekeningBankCount = item?.status_dokumen?.rekening === 1 ? 3 : 0;

        return (
          <div className="exco-province-card" key={`card-${index}`}>
            {/* Card Header */}
            <div className="exco-card-header">
              <div className="exco-card-title-container">
                <div className="exco-card-title">
                  <span className="exco-card-number">{number}.</span>
                  <span className="exco-card-province-name">
                    {getLocationName(item)}
                  </span>
                  {type !== "rtrw" &&
                    type !== "puk" &&
                    item?.is_verified === 1 && (
                      <CheckCircleIcon
                        style={{
                          color: "rgb(240, 101, 36)",
                          fontSize: "20px",
                          marginLeft: "8px",
                        }}
                      />
                    )}
                </div>
                {getLocationSubtitle(item) && (
                  <div className="exco-card-subtitle">
                    {getLocationSubtitle(item)}
                  </div>
                )}
              </div>
              <div className="exco-card-actions">
                <button
                  className="exco-btn-lihat-dokumen"
                  onClick={() => {
                    handleClickDetail(detailParams.tingkat, detailParams.id);
                  }}
                >
                  Lihat Dokumen
                </button>
                {type !== "kelurahan" &&
                  type !== "kecamatan" &&
                  type !== "rtrw" &&
                  type !== "puk" && (
                    <Link
                      to={getTelusuriLink(item)}
                      className="exco-btn-telusuri"
                    >
                      {getTelusuriLabel()}
                    </Link>
                  )}
              </div>
            </div>

            {/* Card Body */}
            <div
              className={`exco-card-body ${type === "kelurahan" || type === "rtrw" || type === "puk" ? "three-columns" : ""}`}
            >
              {/* Anggota Section */}
              <div className="exco-card-section">
                <div className="exco-section-header orange">Anggota</div>

                <div className="exco-data-item">
                  <span className="exco-data-label">
                    {type !== "puk" && type !== "rtrw"
                      ? "Jumlah Anggota Pemilu 2024 s.d 31 Desember 2024"
                      : "Pendaftar"}
                  </span>
                  {/* Use red text and no link if value is 'Belum Terisi' (PUK specific) */}
                  {item?.total_anggota === null ? (
                    <span className="exco-status-belum">Belum Terisi</span>
                  ) : type === "puk" || type === "rtrw" ? (
                    <span className="exco-data-value">
                      {formatNumber(item?.total_anggota)}
                    </span>
                  ) : (
                    <Link to={getMemberListLink(item)}>
                      <span className="exco-data-value link">
                        {formatNumber(item?.total_anggota)}
                      </span>
                    </Link>
                  )}
                </div>

                {/* Anggota Pemilu 2025 - For All EXCO Types */}
                {type !== "puk" && type !== "rtrw" && (
                  <div className="exco-data-item">
                    <span className="exco-data-label">
                      Jumlah Anggota Pemilu 2029 per 1 Januari 2025
                    </span>
                    <Link to={getMemberListLink(item)}>
                      <span className="exco-data-value link">
                        {formatNumber(item?.total_anggota_2029 || 0)}
                      </span>
                    </Link>
                  </div>
                )}

                {/* Target, Selisih, Progress - Only for KabKot */}
                {type === "kabkot" && (
                  <>
                    <div className="exco-data-item">
                      <span className="exco-data-label">Target</span>
                      <span className="exco-data-value">
                        {formatNumber(item?.target_pb)}
                      </span>
                    </div>

                    <div className="exco-data-item">
                      <span className="exco-data-label">Selisih</span>
                      <span className="exco-data-value">
                        {selisih > 0 ? "+" : ""}
                        {formatNumber(selisih)}
                      </span>
                    </div>

                    <div className="exco-data-item">
                      <span className="exco-data-label">Progress</span>
                      <span
                        className={`exco-progress-badge ${getProgressClass(parseInt(progress))}`}
                      >
                        {progress}%
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Kepengurusan Section */}
              <div className="exco-card-section">
                <div className="exco-section-header orange">
                  Kepengurusan {kepengurusanCount}/4 Terisi
                </div>

                <div className="exco-data-item">
                  <span className="exco-data-label">
                    Nomor SK Pengurus Harian
                  </span>
                  <span
                    className={
                      item?.nomer_sk_pengurus === "Belum Terisi"
                        ? "exco-status-belum"
                        : "exco-data-value"
                    }
                  >
                    {item?.nomer_sk_pengurus || "-"}
                  </span>
                </div>

                <div className="exco-data-item">
                  <span className="exco-data-label">
                    Tanggal SK Pengurus Harian
                  </span>
                  <span
                    className={
                      item?.tanggal_sk === "Belum Terisi"
                        ? "exco-status-belum"
                        : "exco-data-value"
                    }
                  >
                    {item?.tanggal_sk === "Belum Terisi"
                      ? "Belum Terisi"
                      : item?.tanggal_sk
                        ? item.tanggal_sk
                            .split("T")[0]
                            .split("-")
                            .reverse()
                            .join("/")
                        : "-"}
                  </span>
                </div>

                <div className="exco-data-item">
                  <span className="exco-data-label">Jumlah Pengurus</span>
                  <span
                    className={
                      item?.total_pengurus === "Belum Terisi"
                        ? "exco-status-belum"
                        : "exco-data-value"
                    }
                  >
                    {item?.total_pengurus || "0"}
                  </span>
                </div>

                {type !== "kecamatan" &&
                  type !== "kelurahan" &&
                  type !== "rtrw" &&
                  type !== "puk" && (
                    <div className="exco-data-item">
                      <span className="exco-data-label">
                        Jumlah Pengurus Perempuan
                      </span>
                      <span className="exco-data-value">
                        {item?.total_pengurus_perempuan
                          ? `${Math.round((parseInt(item?.total_pengurus_perempuan) / parseInt(item?.total_pengurus)) * 100)}%`
                          : "0%"}
                      </span>
                    </div>
                  )}

                <div className="exco-data-item">
                  <span className="exco-data-label">
                    Dokumen SK Pengurus Harian
                  </span>
                  <span
                    className={
                      item?.status_dokumen?.sk_pengurus === 1
                        ? "exco-status-terisi"
                        : "exco-status-belum"
                    }
                  >
                    {item?.status_dokumen?.sk_pengurus === 1
                      ? "Terisi"
                      : "Belum Terisi"}
                  </span>
                </div>

                {type !== "kecamatan" &&
                  type !== "kelurahan" &&
                  type !== "rtrw" &&
                  type !== "puk" && (
                    <div className="exco-data-item">
                      <span className="exco-data-label">
                        Dokumen Suket Kesbangpol
                      </span>
                      <span
                        className={
                          item?.status_dokumen?.kesbangpol === 1
                            ? "exco-status-terisi"
                            : "exco-status-belum"
                        }
                      >
                        {item?.status_dokumen?.kesbangpol === 1
                          ? "Terisi"
                          : "Belum Terisi"}
                      </span>
                    </div>
                  )}
              </div>

              {/* Kantor Section */}
              <div className="exco-card-section">
                <div className="exco-section-header orange">
                  Kantor {kantorCount}/{kantorTotal} Terisi
                </div>

                <div className="exco-data-item">
                  <span className="exco-data-label">Alamat</span>
                  <span
                    className={
                      item?.status_alamat === 1
                        ? "exco-status-terisi"
                        : "exco-status-belum"
                    }
                  >
                    {item?.status_alamat === 1 ? "Terisi" : "Belum Terisi"}
                  </span>
                </div>

                {type !== "rtrw" && (
                  <div className="exco-data-item">
                    <span className="exco-data-label">
                      Foto Kantor {type === "puk" ? "(Opsional)" : ""}
                    </span>
                    <span
                      className={
                        item?.status_dokumen?.foto_kantor === 1
                          ? "exco-status-terisi"
                          : "exco-status-belum"
                      }
                    >
                      {item?.status_dokumen?.foto_kantor === 1
                        ? "Terisi"
                        : "Belum Terisi"}
                    </span>
                  </div>
                )}

                {/* Sewa Kantor & Domisili - Hide only for Kelurahan and RT/RW */}
                {type !== "kelurahan" && type !== "rtrw" && type !== "puk" && (
                  <>
                    <div className="exco-data-item">
                      <span className="exco-data-label">
                        Dokumen Surat Perjanjian Sewa Kantor
                      </span>
                      <span
                        className={
                          item?.status_dokumen?.sewa_kantor === 1
                            ? "exco-status-terisi"
                            : "exco-status-belum"
                        }
                      >
                        {item?.status_dokumen?.sewa_kantor === 1
                          ? "Terisi"
                          : "Belum Terisi"}
                      </span>
                    </div>

                    <div className="exco-data-item">
                      <span className="exco-data-label">
                        Dokumen Surat Domisili Kantor
                      </span>
                      <span
                        className={
                          item?.status_dokumen?.domisili === 1
                            ? "exco-status-terisi"
                            : "exco-status-belum"
                        }
                      >
                        {item?.status_dokumen?.domisili === 1
                          ? "Terisi"
                          : "Belum Terisi"}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Rekening Bank Section - Show for Provinsi, KabKot, and Kecamatan */}
              {type !== "kelurahan" && type !== "rtrw" && type !== "puk" && (
                <div className="exco-card-section">
                  <div className="exco-section-header orange">
                    Rekening Bank {rekeningBankCount}/3 Terisi
                  </div>

                  <div className="exco-data-item">
                    <span className="exco-data-label">No.Rekening</span>
                    <span
                      className={
                        item?.status_dokumen?.rekening === 1
                          ? "exco-status-terisi"
                          : "exco-status-belum"
                      }
                    >
                      {item?.status_dokumen?.rekening === 1
                        ? "Terisi"
                        : "Belum Terisi"}
                    </span>
                  </div>

                  <div className="exco-data-item">
                    <span className="exco-data-label">Nama Bank</span>
                    <span
                      className={
                        item?.status_dokumen?.rekening === 1
                          ? "exco-status-terisi"
                          : "exco-status-belum"
                      }
                    >
                      {item?.status_dokumen?.rekening === 1
                        ? "Terisi"
                        : "Belum Terisi"}
                    </span>
                  </div>

                  <div className="exco-data-item">
                    <span className="exco-data-label">
                      Dokumen Buku Rekening
                    </span>
                    <span
                      className={
                        item?.status_dokumen?.rekening === 1
                          ? "exco-status-terisi"
                          : "exco-status-belum"
                      }
                    >
                      {item?.status_dokumen?.rekening === 1
                        ? "Terisi"
                        : "Belum Terisi"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExcoCardView;
